import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  DynamicDialogRef,
  DynamicDialogConfig,
} from 'primeng/dynamicdialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import {
  ExternalClientService,
  ExternalClientRequestDto,
  ExternalClientResponseDto,
  ExternalClientListItemDto,
} from '../../services/externalclient.service';
import {
  ExternalClientTypeService,
  ExternalClientTypeListItemDto,
} from '../../services/externalclienttype.service';

@Component({
  selector: 'app-externalclient-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>

    <!-- Estado: Formulário -->
    @if (!createdClient) {
      <form [formGroup]="form" class="dialog-form">
        <div class="form-field">
          <label for="name" class="form-label">Nome *</label>
          <input
            pInputText
            id="name"
            type="text"
            formControlName="name"
            placeholder="Nome do cliente externo"
            class="form-input"
            autofocus
          />
          <small *ngIf="isFieldInvalid('name')" class="error-text">
            O nome é obrigatório.
          </small>
        </div>

        <div class="form-field">
          <label for="clientTypeId" class="form-label">Tipo de Cliente *</label>
          <p-dropdown
            id="clientTypeId"
            formControlName="clientTypeId"
            [options]="clientTypes"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecione o tipo"
            styleClass="w-full"
            [filter]="true"
            filterBy="name"
          ></p-dropdown>
          <small *ngIf="isFieldInvalid('clientTypeId')" class="error-text">
            O tipo de cliente é obrigatório.
          </small>
        </div>

        <div class="dialog-actions">
          <p-button
            label="Cancelar"
            severity="secondary"
            [outlined]="true"
            (onClick)="onCancel()"
          ></p-button>
          <p-button
            [label]="isEdit ? 'Atualizar' : 'Salvar'"
            icon="pi pi-check"
            (onClick)="onSave()"
            [disabled]="form.invalid || loading || loadingTypes"
            [loading]="loading"
          ></p-button>
        </div>
      </form>
    }

    <!-- Estado: Sucesso - Exibe API Key -->
    @if (createdClient) {
      <div class="success-container">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h3 class="success-title">Cliente criado com sucesso!</h3>

        <div class="api-key-section">
          <p class="api-key-label">Chave de API (API Key)</p>
          <div class="api-key-display">
            <code class="api-key-value">{{ createdClient.apiKey }}</code>
            <p-button
              icon="pi pi-copy"
              severity="info"
              [text]="true"
              [rounded]="true"
              pTooltip="Copiar API Key"
              (onClick)="copyApiKey()"
            ></p-button>
          </div>
          <p class="api-key-warning">
            <i class="pi pi-exclamation-triangle"></i>
            Copie esta chave agora. Por segurança, ela não será exibida novamente.
          </p>
        </div>

        @if (createdClient.keyPrefix) {
          <div class="prefix-info">
            <span class="prefix-label">Prefixo:</span>
            <code>{{ createdClient.keyPrefix }}</code>
          </div>
        }

        <div class="dialog-actions">
          <p-button
            label="Fechar"
            icon="pi pi-check"
            (onClick)="closeWithResult()"
          ></p-button>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding-top: 8px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .form-label {
      font-weight: 600;
      font-size: 0.9rem;
      color: var(--text-color, #333);
    }

    .form-input {
      width: 100%;
    }

    .error-text {
      color: var(--red-500, #ef4444);
      font-size: 0.8rem;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    /* Sucesso / API Key */
    .success-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 16px 0 8px;
      text-align: center;
    }

    .success-icon {
      font-size: 3rem;
      color: var(--green-500, #22c55e);
    }

    .success-title {
      margin: 0;
      font-size: 1.15rem;
      color: var(--text-color, #333);
    }

    .api-key-section {
      width: 100%;
      background: var(--surface-50, #f8f9fa);
      border: 1px solid var(--surface-200, #dee2e6);
      border-radius: 8px;
      padding: 16px;
    }

    .api-key-label {
      font-weight: 600;
      font-size: 0.85rem;
      color: var(--text-color-secondary, #6c757d);
      margin: 0 0 8px;
    }

    .api-key-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: var(--surface-0, #fff);
      border: 1px dashed var(--surface-300, #ced4da);
      border-radius: 6px;
      padding: 10px 12px;
    }

    .api-key-value {
      font-family: 'Courier New', monospace;
      font-size: 0.9rem;
      word-break: break-all;
      background: transparent;
      padding: 0;
    }

    .api-key-warning {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 0.8rem;
      color: var(--orange-500, #f97316);
      margin: 10px 0 0;
    }

    .prefix-info {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.9rem;
    }

    .prefix-label {
      font-weight: 600;
      color: var(--text-color-secondary, #6c757d);
    }

    :host ::ng-deep .w-full {
      width: 100%;
    }
  `],
})
export class ExternalClientDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  loading = false;
  loadingTypes = false;
  clientTypes: ExternalClientTypeListItemDto[] = [];
  isEdit = false;
  createdClient: ExternalClientResponseDto | null = null;
  private destroyed$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private service: ExternalClientService,
    private typeService: ExternalClientTypeService,
    private messageService: MessageService,
    private clipboard: Clipboard,
    public dialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig
  ) {
    this.isEdit = !!this.dialogConfig.data?.client;
    const client: ExternalClientListItemDto | undefined = this.dialogConfig.data?.client;

    this.form = this.fb.group({
      name: [client?.name || '', [Validators.required, Validators.maxLength(255)]],
      clientTypeId: [client?.typeId || null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loadClientTypes();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadClientTypes(): void {
    this.loadingTypes = true;
    this.typeService
      .list()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.loadingTypes = false))
      )
      .subscribe({
        next: (types) => {
          this.clientTypes = types;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao carregar os tipos de cliente.',
            life: 5000,
          });
        },
      });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const request: ExternalClientRequestDto = {
      name: this.form.value.name.trim(),
      clientTypeId: this.form.value.clientTypeId,
    };

    this.service
      .create(request)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (created) => {
          this.createdClient = created;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao criar o cliente externo.',
            life: 5000,
          });
        },
      });
  }

  copyApiKey(): void {
    if (this.createdClient?.apiKey) {
      this.clipboard.copy(this.createdClient.apiKey);
      this.messageService.add({
        severity: 'info',
        summary: 'Copiado!',
        detail: 'API Key copiada para a área de transferência.',
        life: 2000,
      });
    }
  }

  closeWithResult(): void {
    this.dialogRef.close(this.createdClient);
  }
}
